import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { Card, Col, Container, Image, Row } from "react-bootstrap";
import { eachDayOfInterval, setHours, setMinutes } from "date-fns";
import moment from "moment";

import core from "../../../vendors/core-api";
import PacmanLoading from "../../../components/widgets/PacmanLoading/PacmanLoading";
import { AsyncTypeahead } from "react-bootstrap-typeahead";
import { toAbsoluteUrl } from "../../../../_metronic/_helpers";
import { GRAPH_COLORS } from "../../../constants";
import { KTUtil } from "../../../../_metronic/_assets/js/components/util";
import StatCard from "../../../components/widgets/StatCard";
import { HeaderTextWithActions, SVGIcon } from "../../../components/Base";

const SalesByEventChart = ({ event = {}, analytics = {} }) => {
  const { salesYesterday, salesToday } = analytics;

  const plottedYesterdaySales = new Array(24).fill(0);
  const plottedTodaySales = new Array(24).fill(0);

  salesYesterday.forEach(
    ({ _id, total }) => (plottedYesterdaySales[_id] = total),
  );
  salesToday.forEach(({ _id, total }) => (plottedTodaySales[_id] = total));

  const metrics = {
    series: [
      {
        name: "Yesterday",
        data: plottedYesterdaySales.map((sale, idx) => [
          setHours(setMinutes(new Date(), 0), idx).getTime(),
          sale,
        ]),
      },
      {
        name: "Today",
        data: plottedTodaySales.map((sale, idx) => [
          setHours(setMinutes(new Date(), 0), idx).getTime(),
          sale,
        ]),
      },
    ],
    options: {
      colors: ["#8950fc", ...GRAPH_COLORS],
      chart: {
        type: "area",
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: "smooth",
      },
      xaxis: {
        type: "datetime",
        labels: {
          datetimeUTC: false,
        },
      },
      tooltip: {
        x: {
          format: "HH:mm",
        },
      },
      title: {
        text: `Sales Today (${salesToday.reduce(
          (current, { total }) => current + total,
          0,
        )})`,
      },
    },
  };

  return (
    <Chart
      options={metrics.options}
      series={metrics.series}
      type="area"
      height={"100%"}
      width={"100%"}
    />
  );
};

export const BarChart = ({ event = {}, analytics = {} }) => {
  const { salesByPaymentMethod } = analytics;

  let is5k = false;

  const metric = {
    series: [
      {
        name: "GCash",
        data: salesByPaymentMethod
          .filter(({ _id }) => _id === "gcash")
          .map(({ total }) => {
            if (!is5k) is5k = total >= 5000;
            return is5k
              ? Math.round((total / 1000 + Number.EPSILON) * 100) / 100
              : total;
          }),
      },
      {
        name: "GrabPay",
        data: salesByPaymentMethod
          .filter(({ _id }) => _id === "grab_pay")
          .map(({ total }) => {
            if (!is5k) is5k = total >= 5000;
            return is5k
              ? Math.round((total / 1000 + Number.EPSILON) * 100) / 100
              : total;
          }),
      },
      {
        name: "Card",
        data: salesByPaymentMethod
          .filter(({ _id }) => _id === "card")
          .map(({ total }) => {
            if (!is5k) is5k = total >= 5000;
            return is5k
              ? Math.round((total / 1000 + Number.EPSILON) * 100) / 100
              : total;
          }),
      },
    ],
    options: {
      chart: {
        type: "bar",
        height: 350,
        stacked: true,
      },
      colors: ["#8950fc", ...GRAPH_COLORS],
      title: {
        text: "Sales by Payment Method",
      },
      plotOptions: {
        bar: {
          horizontal: true,
          dataLabels: {
            total: {
              enabled: true,
              offsetX: 0,
              style: {
                fontSize: "13px",
                fontWeight: 900,
              },
            },
          },
        },
      },
      stroke: {
        width: 1,
        colors: ["#fff"],
      },
      xaxis: {
        categories: ["Sales"],
        labels: {
          formatter: function (val) {
            return val + (is5k ? "K" : "");
          },
        },
      },
      yaxis: {
        title: {
          text: undefined,
        },
      },
      tooltip: {
        y: {
          formatter: function (val) {
            return val + (is5k ? "K" : "");
          },
        },
      },
      fill: {
        opacity: 1,
      },
      legend: {
        position: "bottom",
        horizontalAlign: "left",
        offsetX: 40,
      },
    },
  };

  return (
    <Chart
      options={metric.options}
      series={metric.series}
      type="bar"
      height={"100%"}
      width="100%"
    />
  );
};

export const TotalSalesChart = ({
  event = { capacity: 0 },
  analytics = {},
}) => {
  const { salesByEventDay = [] } = analytics;
  const eventDays = eachDayOfInterval({
    start: new Date(event.start_date),
    end: new Date(event.end_date),
  });

  const capacity = event.tickets
    .map(({ capacity, time_slots }) => capacity * time_slots.length)
    .reduce((a, b) => a + b, 0);

  const totalAvailable = capacity * eventDays.length;
  const sold = salesByEventDay
    .map(({ total_sold }) => total_sold)
    .reduce((a, b) => a + b, 0);

  const available = totalAvailable - sold;

  const metrics = {
    series: [sold, available],
    options: {
      chart: {
        type: "donut",
      },
      colors: ["#8950fc", ...GRAPH_COLORS],
      labels: [`Sold`, `Available`],
      dataLabels: {
        formatter: function (val, opts) {
          const value = opts.w.globals.series[opts.seriesIndex];

          return value >= 1000
            ? Math.round((value / 1000 + Number.EPSILON) * 100) / 100 + "K"
            : value;
        },
      },
      legend: {
        show: false,
      },
      plotOptions: {
        pie: {
          startAngle: -90,
          endAngle: 90,
          offsetY: 10,
          donut: {
            labels: {
              show: true,
              total: {
                show: true,
                showAlways: true,
                label: "Percent Sold",
                formatter: function () {
                  return Math.ceil((sold / totalAvailable) * 100) + "%";
                },
              },
            },
          },
        },
      },
      title: {
        text: "Sales Target",
        align: "left",
      },
      grid: {
        padding: {
          bottom: -100,
        },
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            legend: {
              position: "bottom",
            },
          },
        },
      ],
    },
  };

  return (
    <Chart
      options={metrics.options}
      series={metrics.series}
      type="donut"
      height={"100%"}
      width="100%"
    />
  );
};

export const DailySalesChart = ({ event = {}, analytics = {} }) => {
  const { salesByDay = [], averageSalesPerDay = [] } = analytics;
  const expectedDays = eachDayOfInterval({
    start: new Date(salesByDay[0]._id),
    end: new Date(event.end_date),
  });
  const eventDays = eachDayOfInterval({
    start: new Date(event.start_date),
    end: new Date(event.end_date),
  });

  const capacity = event.tickets
    .map(({ capacity, time_slots }) => capacity * time_slots.length)
    .reduce((a, b) => a + b, 0);

  const totalAvailable = capacity * eventDays.length;
  const expected = Math.ceil(totalAvailable / expectedDays.length);

  const metrics = {
    series: [
      {
        name: "Goal",
        data: salesByDay.map(() => expected),
      },
      {
        name: "Average",
        data: averageSalesPerDay.map(({ average }) => average),
      },
      {
        name: "Actual",
        data: salesByDay.map(({ total_sold }) => total_sold),
      },
    ],
    options: {
      chart: {
        height: 350,
        type: "line",
        zoom: {
          enabled: false,
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        width: [5, 7, 5],
        curve: "straight",
        dashArray: [0, 8, 5],
      },
      title: {
        text: "Tickets Sold Per Day",
        align: "left",
      },
      colors: ["#8950fc", ...GRAPH_COLORS],
      legend: {
        tooltipHoverFormatter: function (val, opts) {
          return (
            val +
            " - <strong>" +
            opts.w.globals.series[opts.seriesIndex][opts.dataPointIndex] +
            "</strong>"
          );
        },
      },
      markers: {
        size: 0,
        hover: {
          sizeOffset: 6,
        },
      },
      xaxis: {
        categories: salesByDay.map(({ _id }) =>
          moment(new Date(_id)).format("DD MMM"),
        ),
        labels: {
          datetimeUTC: false,
        },
      },
      tooltip: {
        y: [
          {
            title: {
              formatter: function (val) {
                return val;
              },
            },
          },
          {
            title: {
              formatter: function (val) {
                return val + " per day";
              },
            },
          },
          {
            title: {
              formatter: function (val) {
                return val;
              },
            },
          },
        ],
      },
    },
  };

  return (
    <Chart
      options={metrics.options}
      series={metrics.series}
      type="line"
      height={"100%"}
      width="100%"
    />
  );
};

const AttendanceChart = ({ event, analytics }) => {
  const { attendanceCount } = analytics;

  const scanned = attendanceCount.reduce(
    (current, { total_scanned }) => current + total_scanned,
    0,
  );
  const total = attendanceCount.reduce(
    (current, { total }) => current + total,
    0,
  );

  const metrics = {
    series: [scanned ? Math.ceil((scanned / (scanned + total)) * 100) : 0],
    options: {
      chart: {
        height: 350,
        type: "radialBar",
      },
      plotOptions: {
        radialBar: {
          hollow: {
            size: "70%",
          },
        },
      },
      colors: ["#8950fc", ...GRAPH_COLORS],
      labels: ["Attendance Rate"],
    },
  };

  return (
    <Chart
      options={metrics.options}
      series={metrics.series}
      type="radialBar"
      height={"100%"}
      width={"100%"}
    />
  );
};

const EventsMetrics = ({ store }) => {
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [searchEventLoading, setSearchEventLoading] = useState(false);
  const [eventLists, setEventLists] = useState([]);
  const [event, setEvent] = useState();

  const [metrics, setMetrics] = useState({});

  useEffect(() => {
    async function fetchMetrics() {
      const api = core("pu").get();
      setMetricsLoading(true);

      const { data } = await api.get(
        `/store/${store._id}/event/${event._id}/analytics`,
      );

      setMetrics(data);
      setMetricsLoading(false);
    }

    if (Object.keys(store).length && event) {
      fetchMetrics();
    } else {
      setMetricsLoading(false);
    }
  }, [store, event]);

  const handleSearch = async (query) => {
    setSearchEventLoading(true);
    const api = core("pu").get();

    const { data } = await api.get(`/store/${store._id}/event?search=${query}`);

    setEventLists(data?.events?.docs);
    setSearchEventLoading(false);
  };

  return metricsLoading ? (
    <div className="d-flex align-items-center flex-column p-5 w-30">
      <PacmanLoading />
    </div>
  ) : (
    <>
      <div className="d-flex align-items-end flex-column p-5 w-30">
        <div style={{ width: KTUtil.isMobileDevice() ? "100%" : 280 }}>
          <AsyncTypeahead
            filterBy={() => true}
            id="event-search"
            isLoading={searchEventLoading}
            labelKey="name"
            minLength={3}
            onSearch={handleSearch}
            onChange={([selected]) => setEvent(selected)}
            selected={event ? [event] : []}
            options={eventLists}
            placeholder="Search for an event..."
            renderMenuItemChildren={(option) => (
              <div>
                {option.name}
                <div>
                  <small>{option.location_address}</small>
                </div>
              </div>
            )}
          />
        </div>
      </div>
      {Object.keys(metrics).length ? (
        metrics?.analytics?.[0]?.salesByYear?.length ? (
          <>
            <Container>
              <Row>
                <Col>
                  <HeaderTextWithActions
                    className="mb-8"
                    title={metrics?.event?.name}
                    subtitle={
                      <>
                        <SVGIcon
                          icon="Map/Marker1"
                          title={"Location"}
                          variant="info"
                        />{" "}
                        {metrics?.event?.location_address}{" "}
                        <span className="mr-2"></span>
                      </>
                    }
                  />
                </Col>
              </Row>
              <Row>
                <Col xs={12} sm={4}>
                  <StatCard
                    icon={"Shopping/Money"}
                    variant="white"
                    iconVariant="info"
                    title={"Gross Revenue"}
                    stat={`Php
                    ${metrics?.analytics?.[0]?.salesByYear
                      .reduce((a, { total_income }) => a + total_income, 0)
                      .toLocaleString()}`}
                  />
                </Col>
                <Col xs={12} sm={4}>
                  <StatCard
                    iconVariant="info"
                    icon={"General/Visible"}
                    variant="white"
                    title={"Total Views"}
                    stat={50}
                  />
                </Col>
                <Col xs={12} sm={4}>
                  <StatCard
                    iconVariant="info"
                    icon={"General/Star"}
                    variant="white"
                    title={"Rating"}
                    stat={
                      metrics?.analytics?.[0]?.ratingAverage?.reduce(
                        (current, { average = 0 }) => average + current,
                        0,
                      ) || "No Rating"
                    }
                  />
                </Col>
              </Row>
              <Row xs={1} md={2} className="pt-4">
                <Col>
                  <Card style={{ height: 350 }}>
                    <Card.Body>
                      <TotalSalesChart
                        event={metrics?.event}
                        analytics={metrics?.analytics?.[0]}
                      />
                    </Card.Body>
                  </Card>
                </Col>
                <Col>
                  <Card style={{ height: 350 }}>
                    <Card.Body>
                      <DailySalesChart
                        event={metrics?.event}
                        analytics={metrics?.analytics?.[0]}
                      />
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              <Row xs={1} md={3} className="pt-4 pb-4">
                <Col>
                  <Card style={{ height: 350 }}>
                    <Card.Body>
                      <AttendanceChart
                        event={metrics?.event}
                        analytics={metrics?.analytics?.[0]}
                      />
                    </Card.Body>
                  </Card>
                </Col>
                <Col>
                  <Card style={{ height: 350 }}>
                    <Card.Body>
                      <BarChart
                        event={metrics?.event}
                        analytics={metrics?.analytics?.[0]}
                      />
                    </Card.Body>
                  </Card>
                </Col>
                <Col>
                  <Card style={{ height: 350 }}>
                    <Card.Body>
                      <SalesByEventChart
                        event={metrics?.event}
                        analytics={metrics?.analytics?.[0]}
                      />
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Container>
          </>
        ) : (
          <Container>
            <Row>
              <Col>
                <div className="text-center p-5">
                  <h6>It seems like this event has no data yet.</h6>
                  <Image
                    src={toAbsoluteUrl("/media/misc/empty_data.jpg")}
                    style={{ width: KTUtil.isMobileDevice() ? "100%" : 450 }}
                  />
                </div>
              </Col>
            </Row>
          </Container>
        )
      ) : (
        <Container>
          <Row>
            <Col>
              <div className="text-center p-5">
                <h6>Start by searching for an event in the search bar.</h6>
                <Image
                  src={toAbsoluteUrl("/media/misc/data.jpg")}
                  style={{ width: KTUtil.isMobileDevice() ? "100%" : 450 }}
                />
              </div>
            </Col>
          </Row>
        </Container>
      )}
    </>
  );
};

export default EventsMetrics;
